import { DEG_TO_RAD, Point } from './skillFitDonutGeometry';

/** Flat on a table, so plain screen-down gravity. */
export const DEFAULT_GRAVITY: Point = { x: 0, y: 1 };

/** Below this tilt magnitude the reading is noise, so fall back to straight down. */
export const TILT_DEADZONE = 0.05;
/** Exponential moving average factor. Raw 60Hz sensor output makes the marbles buzz. */
export const TILT_SMOOTHING = 0.15;

const clamp = (value: number, low: number, high: number) => Math.min(Math.max(value, low), high);

/**
 * Maps DeviceOrientationEvent beta/gamma to a gravity vector in viewport coordinates (+y down).
 * Both are clamped to +/-90 because Android reports the full +/-180 beta range and past vertical
 * `gamma` inverts, which would flip gravity sideways. `devicemotion` is avoided because its
 * accelerationIncludingGravity has a known iOS/Android sign inversion.
 */
export const orientationToGravity = (
    beta: number | null | undefined,
    gamma: number | null | undefined,
    screenAngle = 0,
): Point => {
    const safeBeta = clamp(beta ?? 0, -90, 90);
    const safeGamma = clamp(gamma ?? 0, -90, 90);

    let x = Math.sin(safeGamma * DEG_TO_RAD);
    let y = Math.sin(safeBeta * DEG_TO_RAD);

    // Rotate device axes into viewport axes for landscape and upside-down orientations.
    const rotation = -screenAngle * DEG_TO_RAD;
    const cos = Math.cos(rotation);
    const sin = Math.sin(rotation);
    [x, y] = [x * cos - y * sin, x * sin + y * cos];

    const magnitude = Math.hypot(x, y);
    if (magnitude < TILT_DEADZONE) {
        return DEFAULT_GRAVITY;
    }

    const scale = Math.min(1, magnitude) / magnitude;
    return { x: x * scale, y: y * scale };
};

export const smoothGravity = (previous: Point, next: Point, factor = TILT_SMOOTHING): Point => ({
    x: previous.x + (next.x - previous.x) * factor,
    y: previous.y + (next.y - previous.y) * factor,
});

export type TiltPermission = 'unsupported' | 'prompt' | 'granted' | 'denied';

export const TILT_PERMISSION_STORAGE_KEY = 'skill-fit-tilt-permission';

interface DeviceOrientationEventWithPermission {
    requestPermission?: () => Promise<'granted' | 'denied'>;
}

const orientationEventConstructor = (): DeviceOrientationEventWithPermission | undefined =>
    typeof window === 'undefined'
        ? undefined
        : (window as unknown as { DeviceOrientationEvent?: DeviceOrientationEventWithPermission })
                .DeviceOrientationEvent;

export const supportsDeviceOrientation = (): boolean => Boolean(orientationEventConstructor());

/**
 * Whether the device has a touchscreen at all, needed because the permission API is a *browser*
 * capability and a desktop browser can expose `requestPermission` with no gyroscope behind it.
 * `any-pointer` rather than `pointer`, since `pointer` describes the primary input and would
 * strand an iPad driven through a trackpad.
 */
export const isTouchCapableDevice = (): boolean => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return false;
    }

    return window.matchMedia('(any-pointer: coarse)').matches === true;
};

/** iOS 13+ gates the sensor behind an explicit, gesture-initiated permission call. */
export const requiresTiltPermission = (): boolean =>
    typeof orientationEventConstructor()?.requestPermission === 'function';

/**
 * What the platform has already told us. A denial is remembered as well as a grant, because iOS
 * shows its dialog once per origin: after a "Cancel" every later request resolves denied with
 * nothing on screen. Rejections are never stored, see `requestTiltPermission`.
 */
const readStoredDecision = (): TiltPermission | null => {
    try {
        const stored = localStorage.getItem(TILT_PERMISSION_STORAGE_KEY);

        return stored === 'granted' || stored === 'denied' ? stored : null;
    }
    catch {
        return null;
    }
};

const storeDecision = (decision: 'granted' | 'denied') => {
    try {
        localStorage.setItem(TILT_PERMISSION_STORAGE_KEY, decision);
    }
    catch {
        // localStorage unavailable in private mode. The prompt just reappears next load.
    }
};

/**
 * Drops a remembered grant, so the next load offers the affordance again. Called both on a denial
 * and when a supposedly-granted sensor turns out to be silent.
 */
export const clearStoredTiltGrant = () => {
    try {
        localStorage.removeItem(TILT_PERMISSION_STORAGE_KEY);
    }
    catch {
        // An unreadable store already behaves as "no grant remembered".
    }
};

export const initialTiltPermission = (): TiltPermission => {
    // No touchscreen means no tilt affordance and no listener, whatever the browser exposes.
    if (!supportsDeviceOrientation() || !isTouchCapableDevice()) {
        return 'unsupported';
    }
    if (!requiresTiltPermission()) {
        // Android and desktop expose the sensor without a permission call.
        return 'granted';
    }
    // A remembered grant is an assumption, not a fact, since iOS does not reliably keep its own.
    // The sim verifies it and reverts to 'prompt' if the sensor stays silent.
    return readStoredDecision() ?? 'prompt';
};

/**
 * Must be called from inside a user-gesture handler, over HTTPS.
 *
 * A resolved 'denied' is the platform's answer and is remembered. A rejection means we never got
 * to ask, from no gesture or an insecure origin, so it stores nothing and stays at 'prompt'.
 */
export const requestTiltPermission = async (): Promise<TiltPermission> => {
    const constructor = orientationEventConstructor();

    if (!constructor) {
        return 'unsupported';
    }
    if (typeof constructor.requestPermission !== 'function') {
        return 'granted';
    }

    try {
        // Overwrites, so a grant the platform has since refused is replaced rather than cleared.
        const result = await constructor.requestPermission();
        storeDecision(result);

        return result;
    }
    catch {
        return 'prompt';
    }
};

export const currentScreenAngle = (): number => {
    if (typeof window === 'undefined') {
        return 0;
    }
    return window.screen?.orientation?.angle ?? 0;
};
